import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FullScreenSpinner } from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/Badge';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import StepProgress from '../../components/candidates/StepProgress';
import {
  StepIconRow,
  StepDetailPanel,
  useSelectedStep,
  formatAnswer,
  needsConversionReview,
  missingConversionDetails,
} from '../../components/candidates/StepTimeline';
import DocumentsTab from '../../components/candidates/DocumentsTab';
import CandidateProfileTab from '../../components/candidates/CandidateProfileTab';
import ActivityTab from '../../components/candidates/ActivityTab';
import WithdrawDialog from '../../components/candidates/WithdrawDialog';
import ChangeCategoryModal from '../../components/candidates/ChangeCategoryModal';
import ConvertVehicleTypeModal, { targetVehicleCategory } from '../../components/candidates/ConvertVehicleTypeModal';
import Tabs from '../../components/ui/Tabs';
import Can from '../../auth/Can';
import { getCandidate, withdrawCandidate, reopenCandidate } from '../../api/candidates';
import { listWorkflowCategories } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

const TABS = [
  { value: 'documents', label: 'Documents' },
  { value: 'profile', label: 'Profile' },
  { value: 'activity', label: 'Activity' },
];

const TERMINAL_STATUSES = ['rejected', 'withdrawn', 'hired'];

function OverflowMenu({ candidate }) {
  const [open, setOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [changeCategoryOpen, setChangeCategoryOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const menuRef = useRef(null);
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const convertTarget = targetVehicleCategory(candidate.category, categoriesQuery.data);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });

  const withdrawMutation = useMutation({
    mutationFn: (reason) => withdrawCandidate(candidate.id, reason),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Candidate withdrawn.' });
      setWithdrawOpen(false);
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not withdraw.' }),
  });

  const reopenMutation = useMutation({
    mutationFn: () => reopenCandidate(candidate.id),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Candidate reopened.' });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reopen.' }),
  });

  const canChangeCategory = candidate.steps.every((s) => s.code === 'registration' || s.status === 'pending');

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
      >
        Actions ▾
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <Can perm="candidates.withdraw">
            {!TERMINAL_STATUSES.includes(candidate.status) && (
              <button
                onClick={() => {
                  setOpen(false);
                  setWithdrawOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Withdraw
              </button>
            )}
          </Can>
          <Can perm="candidates.reopen">
            {(candidate.status === 'withdrawn' || candidate.status === 'rejected') && (
              <button
                onClick={() => {
                  setOpen(false);
                  reopenMutation.mutate();
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Reopen
              </button>
            )}
          </Can>
          <Can perm="candidates.change-category">
            {canChangeCategory && (
              <button
                onClick={() => {
                  setOpen(false);
                  setChangeCategoryOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Change category
              </button>
            )}
          </Can>
          <Can perm="candidates.convert-vehicle-type">
            {!TERMINAL_STATUSES.includes(candidate.status) && convertTarget && (
              <button
                onClick={() => {
                  setOpen(false);
                  setConvertOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Convert to {candidate.category.vehicle_type === 'taxi' ? 'Bike' : 'Taxi'}
              </button>
            )}
          </Can>
        </div>
      )}

      <WithdrawDialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onConfirm={(reason) => withdrawMutation.mutate(reason)}
        loading={withdrawMutation.isPending}
      />
      <ChangeCategoryModal open={changeCategoryOpen} onClose={() => setChangeCategoryOpen(false)} candidate={candidate} />
      <ConvertVehicleTypeModal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        candidate={candidate}
        categories={categoriesQuery.data}
      />
    </div>
  );
}

export default function CandidateWorkspace() {
  const { id } = useParams();
  const [tab, setTab] = useState('documents');

  const candidateQuery = useQuery({ queryKey: ['candidates', id], queryFn: () => getCandidate(id) });

  if (candidateQuery.isLoading) return <FullScreenSpinner />;
  if (candidateQuery.isError) return <EmptyState title="Candidate not found" />;

  const candidate = candidateQuery.data;

  return <CandidateWorkspaceContent candidate={candidate} tab={tab} setTab={setTab} />;
}

function hasAnswer(value) {
  return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
}

function CandidateWorkspaceContent({ candidate, tab, setTab }) {
  const [selectedStep, setSelectedStepId] = useSelectedStep(candidate);

  const basicDetailFields = (candidate.steps || []).flatMap((step) =>
    (step.fields || [])
      .filter((f) => f.show_in_basic_details && hasAnswer(step.answers?.[f.id]))
      .map((f) => ({ key: `${step.workflow_step_id}:${f.id}`, label: f.label, value: formatAnswer(step.answers[f.id]) }))
  );

  const overdueStep = (candidate.steps || []).find((s) => s.is_current && s.is_overdue);
  const conversionReviewSteps = (candidate.steps || []).filter(needsConversionReview);

  return (
    <div>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{candidate.full_name}</h1>
              <StatusBadge status={candidate.status} />
              <CategoryBadge category={candidate.category} />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">{candidate.reference_no}</p>
          </div>
          <OverflowMenu candidate={candidate} />
        </div>

        {candidate.status === 'rejected' && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            Recruitment closed — rejected. {candidate.rejection_reason}
          </div>
        )}
        {candidate.status === 'withdrawn' && (
          <div className="mt-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            Withdrawn. {candidate.rejection_reason}
          </div>
        )}
        {candidate.status === 'hired' && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            Hired{candidate.employee_no ? ` — Employee No ${candidate.employee_no}` : ''}.
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-3 text-sm">
          {candidate.progress.current_sort_order && (
            <div className="w-40">
              <StepProgress current={candidate.progress.current_sort_order} total={candidate.progress.total_steps} />
            </div>
          )}
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Nationality</span>{' '}
            <span className="text-gray-800">{candidate.nationality}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Passport No</span>{' '}
            <span className="text-gray-800">{candidate.passport_no}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Contact No</span>{' '}
            <span className="text-gray-800">{candidate.contact_no}</span>
          </div>
          {candidate.employee_no && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-400">Employee No</span>{' '}
              <span className="text-gray-800">{candidate.employee_no}</span>
            </div>
          )}
          {basicDetailFields.map((f) => (
            <div key={f.key}>
              <span className="text-xs uppercase tracking-wide text-gray-400">{f.label}</span>{' '}
              <span className="text-gray-800">{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-900">Workflow Steps</h2>
        {conversionReviewSteps.length > 0 && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <span className="font-semibold">Needs review after conversion</span> — carried over but still missing what
            the new workflow requires:
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {conversionReviewSteps.map((s) => (
                <li key={s.workflow_step_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedStepId(s.candidate_step_id)}
                    className="font-medium underline hover:no-underline"
                  >
                    {s.title}
                  </button>
                  : {missingConversionDetails(s).join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {overdueStep && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span className="font-semibold">Overdue:</span> "{overdueStep.title}" has been in progress for{' '}
            {overdueStep.days_overdue} day{overdueStep.days_overdue === 1 ? '' : 's'} past its {overdueStep.sla_days}-day SLA.
          </div>
        )}
        <StepIconRow candidate={candidate} selectedStep={selectedStep} onSelect={setSelectedStepId} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <StepDetailPanel candidate={candidate} selectedStep={selectedStep} />
          </div>
        </div>

        <div>
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
            <div className="mt-4">
              {tab === 'documents' && <DocumentsTab candidate={candidate} />}
              {tab === 'profile' && <CandidateProfileTab candidate={candidate} />}
              {tab === 'activity' && <ActivityTab candidate={candidate} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
